/** Historical BOM: completeness check, search index, search, and BOM → cost sheet (original applyBom). */
import { f2, f3, n } from "./num.ts";
import { blankSheet, newRow } from "./costing.ts";
import { bigImg, cleanDesc, cleanPos, fabCatOf, trimCatOf, unitOf } from "./text.ts";
import { L, type BomComp, type BomText, type History, type IndexItem, type Lang, type QCoef, type Sheet } from "./types.ts";

/** Lines with a real material code (code NULL / empty is skipped). */
export function bomLines(comps: BomComp[] | undefined): BomComp[] {
  return (comps || []).filter((c) => c[0] && c[0] !== "NULL");
}

/**
 * BOM lines in cost-sheet order (JF 2026-10-12): fabrics first, then trims, each in the BOM's own order — exactly the
 * order the rows land in the sheet, so the BOM shown next to the sheet reads top to bottom the same way.
 */
export function bomOrder(cps: BomComp[]): BomComp[] {
  return [...cps.filter((c) => c[6] === "fabric"), ...cps.filter((c) => c[6] !== "fabric")];
}

export const lineTotal = (cps: BomComp[]): number => cps.reduce((a: number, c) => a + n(c[4]), 0);

/** |Σ line − SC material cost| ≤ max(2, 25% of material cost). */
export const totalMatches = (lt: number, mat: unknown): boolean => Math.abs(lt - n(mat)) <= Math.max(2, n(mat) * 0.25);

/** Complete = has a fabric line and the line total matches the SC material cost. */
export function checkBom(cps: BomComp[], mat: unknown): { hasF: boolean; lineTotal: number; okT: boolean; ok: boolean } {
  const hasF = cps.some((c) => c[6] === "fabric");
  const lt = lineTotal(cps);
  const okT = totalMatches(lt, mat);
  return { hasF, lineTotal: lt, okT, ok: hasF && okT };
}

/** Lower-cased search text: sc, style, customer, sub, category, description, gender, factory, country. */
export function makeHay(x: Pick<IndexItem, "sc" | "style" | "cust" | "sub" | "cat" | "desc" | "gender" | "fac" | "co">): string {
  return (x.sc + " " + x.style + " " + x.cust + " " + x.sub + " " + x.cat + " " + x.desc + " " + x.gender + " " + x.fac + " " + x.co).toLowerCase();
}

const collator = new Intl.Collator("en");
/** Display order: complete first, then with picture, then SC descending. */
export function compareIndex(a: IndexItem, b: IndexItem): number {
  return (Number(b.ok) - Number(a.ok)) || ((b.img ? 1 : 0) - (a.img ? 1 : 0)) || collator.compare(String(b.sc), String(a.sc));
}

export interface SearchQuery { q?: string; cat?: string; cu?: string; ok?: boolean; img?: boolean }

/**
 * Filter the index: every word must appear in the search text (AND); cat is "c:<category>" or "s:<sub-category>";
 * customer is a substring match; ok = complete BOMs only; img = with picture only.
 */
export function searchIndex(index: IndexItem[], sq: SearchQuery): IndexItem[] {
  const ws = String(sq.q || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  const cu = String(sq.cu || "").trim().toLowerCase();
  const ca = sq.cat || "";
  return index.filter((x) => {
    // Every item in the historical index has BOM rows.  `ok` represents the
    // separate amount-reconciliation check and must not hide a usable BOM.
    if (sq.img && !x.img) return false;
    if (cu && x.cust.toLowerCase().indexOf(cu) < 0) return false;
    if (ca) {
      if (ca.indexOf("c:") === 0 && x.cat !== ca.slice(2)) return false;
      if (ca.indexOf("s:") === 0 && x.sub !== ca.slice(2)) return false;
    }
    for (const w of ws) if (x.hay.indexOf(w) < 0) return false;
    return true;
  });
}

export type ApplyMode = "replace" | "add";

/** Copy permitted descriptions and the SC-level CM; restricted line prices/usages stay genuinely blank. */
export function bomTextToSheet(args: Omit<Parameters<typeof bomToSheet>[0], "comps"> & { lines: BomText[] }): Sheet {
  const comps: BomComp[] = args.lines.map((c) => [c.code, null, c.unit, null, null, c.description, c.kind, c.type, c.placement, c.main, "", c.supplierArticle]);
  const s = bomToSheet({ ...args, comps });
  for (const kind of ["fab", "trim"] as const) {
    const start = args.mode === "add" ? args.sheet[kind].length : 0;
    s[kind].slice(start).forEach((r) => { r.px = ""; r.yld = ""; r.src.px = null; r.src.yld = null; });
  }
  s.bomWarn = L(args.lang,
    `Historical BOM descriptions only. Unit prices and usages were not imported. SC reference: materials $${f2(args.item.mat)}, CM $${f2(args.item.cm)}, total FOB cost $${f2(args.item.fob)}. Enter your own prices and usages before costing.`,
    `仅导入历史 BOM 文字描述，未导入单价和用量。SC 参考：总材料成本 $${f2(args.item.mat)}、CM $${f2(args.item.cm)}、FOB 总成本 $${f2(args.item.fob)}。请填写本次单价和用量后再计算成本。`);
  return s;
}

/**
 * Put an SC's BOM into the sheet. "replace" starts a fresh sheet (keeps Quote mode) and fills the header;
 * "add" appends the lines and only sets CM if the sheet has none.
 * `only` = the lines to take (indexes into bomOrder(bomLines(comps)), the order the BOM is shown in) — JF 2026-10-12:
 * pick rows one by one instead of adding the whole BOM and deleting the extra rows.
 */
export function bomToSheet(args: { sheet: Sheet; item: IndexItem; comps: BomComp[]; mode: ApplyMode; lang: Lang; hist: History; today?: string; coef?: QCoef; only?: number[] }): Sheet {
  const { item: x, lang, hist } = args;
  const sc = x.sc;
  const all = bomOrder(bomLines(args.comps));
  const cps = args.only ? all.filter((_, i) => args.only!.includes(i)) : all;
  let s: Sheet;
  // replace keeps Quote mode and the sheet's own coefficients (else the given defaults; none = v2 formulas)
  if (args.mode === "replace") { s = blankSheet(args.today, args.sheet.qcoef || args.coef); s.quote = args.sheet.quote; } else s = structuredClone(args.sheet);
  s.from = "bom";
  const tag = "SC " + sc + (x.style ? " · " + x.style : "");
  for (const c of cps) {
    const isF = c[6] === "fabric";
    const r = newRow(isF ? "fab" : "trim");
    const desc = cleanDesc(c[5]);
    if (isF) {
      // fabric: name = supplier article, position = placement, quality = composition / construction
      r.name = String(c[11] || c[0]); r.pos = cleanPos(c[8]); r.unit = unitOf(c[2]); r.qual = desc.slice(0, 90);
      r.cat = fabCatOf(desc, hist); r.rem = L(lang, "Material code ", "物料号 ") + c[0];
    } else {
      // trim: name = description, supplier = (not in history), quality = supplier article, category = trim type
      r.name = String(desc.slice(0, 70) || c[7] || c[0]); r.pos = cleanPos(c[8]); r.supp = ""; r.qual = String(c[11] || "");
      r.cat = trimCatOf(c[7], hist); r.rem = L(lang, "Material code ", "物料号 ") + c[0] + (r.cat ? "" : " · " + (c[7] || ""));
    }
    r.px = f3(c[3]); r.yld = f3(c[1]);
    r.src = {
      name: { t: "hist", d: tag },
      px: { t: "hist", d: tag + " · " + L(lang, "unit price", "单价") + " $" + f3(c[3]) },
      yld: { t: "hist", d: tag + " · " + L(lang, "usage", "用量") + " " + f3(c[1]) + " " + (c[2] || "") },
    };
    (isF ? s.fab : s.trim).push(r);
  }
  if (args.mode === "replace" || !s.cm.v) {
    s.cm = { v: f2(x.cm), note: "SC " + sc + (x.fac ? " · " + x.fac : "") + (x.qty ? " · " + x.qty + " pcs" : ""), src: { t: "hist", d: tag + " · CM $" + f2(x.cm) }, q: null, chk: "" };
  }
  const fabs = cps.filter((c) => c[6] === "fabric");
  const mf = fabs.filter((c) => c[9])[0] || fabs[0];
  if (args.mode === "replace") {
    if (mf) { s.h.content = String(mf[5] || "").split(";")[0]; s.h.article = String(mf[11] || mf[0]); }
    s.h.customer = x.cust; s.h.style = x.style; s.h.name = (x.desc ? x.desc + " · " : "") + x.sub; s.h.factory = x.fac; s.h.co = x.co;
    s.h.qty = String(x.qty || ""); if (x.img) s.sketch = bigImg(x.img);
  }
  const ls = lineTotal(all); // the check is about the BOM as a whole, also when only some rows are taken
  s.bomWarn = totalMatches(ls, x.mat) ? "" : L(lang,
    "Note: the BOM lines of " + sc + " add up to $" + f2(ls) + " but the SC's material cost is $" + f2(x.mat) + " — the BOM may list several colourways or per-order quantities. Check the yields before using.",
    "注意：" + sc + " 的 BOM 行合计 $" + f2(ls) + "，但该 SC 的物料成本是 $" + f2(x.mat) + "——BOM 可能包含多个颜色或整单用量，使用前请核对用量。");
  s.origin = tag;
  return s;
}
